import { useEffect, useMemo } from "react";
import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { ApolloError, ApolloQueryResult } from "@apollo/client";
import { useSession } from "next-auth/react";
import { Cart, LineItem } from "src/types/ctgraphql.d";
import { locale, storeKey } from "src/config/app-config";

export interface CartVars {
  locale: string;
  storeKey: string;
}

interface CartHook {
  data: { cart: Cart | null };
  loading: boolean;
  hasLoaded: boolean;
  error?: ApolloError;
  refetchCart: (
    variables?: Partial<CartVars> | undefined
  ) => Promise<ApolloQueryResult<CartData>>;
}
export interface CartData {
  inStore: { me: { activeCart: Cart | null } };
}

export const moneyFragment = gql`
  fragment money on Money {
    type
    currencyCode
    centAmount
    fractionDigits
  }
`;

export const addressFragment = gql`
  fragment address on Address {
    firstName
    lastName
    phone
    email
    streetName
    streetNumber
    additionalStreetInfo
    postalCode
    city
    state
    country
    additionalAddressInfo
  }
`;

export const cartFragment = gql`
  fragment cart on Cart {
    id
    anonymousId
    version
    createdAt
    customerEmail
    customerId
    inventoryMode
    discountCodes {
      discountCodeRef {
        typeId
        id
      }
      state
      discountCode {
        id
        code
        isActive
        name(locale: $locale)
        description(locale: $locale)
        cartDiscounts {
          id
          key
          target {
            type
          }
          value {
            ... on AbsoluteDiscountValue {
              money {
                ...money
              }
            }
            ... on FixedPriceDiscountValue {
              money {
                ...money
              }
            }
            ... on RelativeDiscountValue {
              permyriad
            }
            ... on GiftLineItemValue {
              variantId
            }
          }
        }
      }
    }
    totalPrice {
      ...money
    }
    taxedPrice {
      totalNet {
        ...money
      }
      totalGross {
        ...money
      }
      totalTax {
        ...money
      }
    }
    lineItems {
      id
      productId
      name(locale: $locale)
      productSlug(locale: "en")
      quantity
      price {
        value {
          ...money
        }
      }
      discountedPricePerQuantity {
        quantity
        discountedPrice {
          value {
            ...money
          }
          includedDiscounts {
            discountedAmount {
              ...money
            }
            discount {
              id
              key
            }
          }
        }
      }
      totalPrice {
        ...money
      }
      variant {
        sku
        prices {
          id
          country
          value {
            ...money
          }
          custom {
            customFieldsRaw {
              name
              value
            }
          }
        }
        images {
          url
          dimensions {
            width
            height
          }
          label
        }
        availability {
          channels {
            results {
              availability {
                id
                isOnStock
                availableQuantity
              }
              channel {
                id
              }
            }
          }
        }
        attributesRaw {
          name
          value
        }
      }
      supplyChannel {
        id
      }
    }
    customLineItems {
      totalPrice {
        ...money
      }
      name(acceptLanguage: [$locale, "en"])
      slug
    }
    shippingAddress {
      ...address
    }
    billingAddress {
      ...address
    }
    shippingInfo {
      shippingMethod {
        id
        key
        name
      }
      shippingRate {
        price {
          ...money
        }
        freeAbove {
          ...money
        }
      }
      price {
        ...money
      }
      discountedPrice {
        value {
          ...money
        }
      }
    }
    country
    store {
      key
    }
    origin
    custom {
      customFieldsRaw {
        name
        value
      }
    }
  }

  ${moneyFragment}
  ${addressFragment}
`;

const CART = gql`
  query cart($locale: Locale!, $storeKey: KeyReferenceInput!) {
    inStore(key: $storeKey) {
      me {
        activeCart {
          ...cart
        }
      }
    }
  }

  ${cartFragment}
`;

function formatLineItems(cart: Cart, lineItems?: LineItem[]) {
  const newItems = lineItems?.map((item) => ({
    ...item,
    currency: formatCurrency(cart),
  }));
  return newItems || [];
}

function formatCurrency(cart: Cart) {
  return { code: cart?.totalPrice.currencyCode, symbol: "", position: "" };
}

const useCart = ({
  skip,
  onCompleted,
}: {
  onCompleted?: (cartData: CartData) => void;
  skip?: boolean;
} = {}): CartHook => {
  const { data: session } = useSession();
  const { loading, error, data, refetch } = useQuery<CartData, CartVars>(CART, {
    variables: {
      locale,
      storeKey,
    },
    context: { useCT: true, token: session?.accessToken || "" },
    fetchPolicy: "network-only",
    skip: !session || !session.accessToken || skip,
    onCompleted: (cartData) => {
      if (onCompleted) {
        onCompleted(cartData);
      }
    },
  });

  useEffect(() => {
    const refetchCart = () => {
      if (!document.hidden && refetch) {
        void refetch();
      }
    };

    window.addEventListener("visibilitychange", refetchCart, false);

    return () => {
      window.removeEventListener("visibilitychange", refetchCart);
    };
  }, [refetch]);

  const formattedData = useMemo(
    () => ({
      cart: data?.inStore.me.activeCart
        ? {
            ...data.inStore.me.activeCart,
            lineItems: formatLineItems(
              data?.inStore.me.activeCart,
              data.inStore.me.activeCart.lineItems
            ),
          }
        : null,
    }),
    [data?.inStore.me.activeCart]
  );

  return {
    loading,
    error,
    hasLoaded: data !== undefined,
    data: formattedData,
    refetchCart: refetch,
  };
};

export { useCart };
